import { useCallback } from 'react';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useService } from '@atlassian/help-center-common-util/use-service';
import { usePfErrorUtils } from '@atlassian/proforma-common-core/error-utils-context';

import { usePortalId } from '../../controllers/request-create-context-provider';
import { proformaGasAnalyticsFetch } from '../../common/services/proforma-gas-analytics-fetch';
import { JSM_CUSTOMER_PORTAL_URL_PREFIX } from '../constants';
import type { RegisterAsTemporaryAttachmentResponse } from './types';

/**
 * Service hook for registering uploaded files as temporary attachments.
 *
 * When a file is uploaded to the media service, it needs to be registered
 * with JSM so that it can be attached to the request when the form is submitted.
 *
 * Uses portalId because this calls a JSM customer portal REST API
 * (POST /rest/servicedesk/1/customer/portal/{portalId}/media/upload),
 * NOT a ProForma Stargate endpoint.
 *
 * See docs/PORTAL_ID_AND_SERVICE_DESK_ID.md for the distinction.
 */
export const useRegisterAsTemporaryAttachmentService = () => {
	di(usePortalId);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const errorUtils = usePfErrorUtils();
	const portalId = usePortalId();

	const request = useCallback(
		({ fileId }: { fileId: string }) =>
			proformaGasAnalyticsFetch<RegisterAsTemporaryAttachmentResponse>(
				`${JSM_CUSTOMER_PORTAL_URL_PREFIX}/${portalId}/media/upload`,
				{
					method: 'POST',
					body: JSON.stringify({ fileId }),
				},
				createAnalyticsEvent,
				errorUtils,
			),
		[portalId, createAnalyticsEvent, errorUtils],
	);

	return useService(request);
};
